import React, { useState, useMemo } from 'react';
import { Tree, Button, Input, Space, Dropdown, Modal, Tooltip, Badge, Typography } from 'antd';
import type { DataNode, TreeProps } from 'antd/es/tree';
import {
  FolderOutlined,
  FolderOpenOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import type { FolderTreeNode } from '../models';

const { Text } = Typography;

interface FolderTreeProps {
  tree: FolderTreeNode[];
  selectedKey: string | null;
  expandedKeys: string[];
  loading?: boolean;
  onSelect: (folderID: string | null) => void;
  onExpandedKeysChange: (keys: string[]) => void;
  onCreate: (name: string, parentID?: string | null) => void;
  onRename: (id: string, name: string) => void;
  onDelete: (id: string) => void;
}

const FolderTree: React.FC<FolderTreeProps> = ({
  tree,
  selectedKey,
  expandedKeys,
  loading,
  onSelect,
  onExpandedKeysChange,
  onCreate,
  onRename,
  onDelete,
}) => {
  const [creatingInParent, setCreatingInParent] = useState<string | null | undefined>(undefined);
  const [newFolderName, setNewFolderName] = useState('');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');

  const handleCreateStart = (parentID?: string | null) => {
    setCreatingInParent(parentID ?? null);
    setNewFolderName('');
  };

  const handleCreateConfirm = () => {
    const name = newFolderName.trim();
    if (name) {
      onCreate(name, creatingInParent);
    }
    setCreatingInParent(undefined);
    setNewFolderName('');
  };

  const handleRenameStart = (id: string, currentName: string) => {
    setRenamingId(id);
    setRenameValue(currentName);
  };

  const handleRenameConfirm = () => {
    if (renamingId && renameValue.trim()) {
      onRename(renamingId, renameValue.trim());
    }
    setRenamingId(null);
    setRenameValue('');
  };

  const handleDeleteConfirm = (id: string, name: string) => {
    Modal.confirm({
      title: '確認刪除資料夾',
      content: `確定要刪除資料夾「${name}」嗎？資料夾內的所有子資料夾、知識條目、檔案文件及相關向量資料都將被永久刪除，此操作無法復原。`,
      okText: '確認刪除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: () => onDelete(id),
    });
  };

  const getContextMenuItems = (node: FolderTreeNode) => [
    {
      key: 'new-sub',
      icon: <PlusOutlined />,
      label: '新增子資料夾',
      onClick: () => handleCreateStart(node.id),
    },
    {
      key: 'rename',
      icon: <EditOutlined />,
      label: '重新命名',
      onClick: () => handleRenameStart(node.id, node.name),
    },
    { type: 'divider' as const },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: '刪除',
      danger: true,
      onClick: () => handleDeleteConfirm(node.id, node.name),
    },
  ];

  // Build lookup map for context menus
  const nodeMap = useMemo(() => {
    const map = new Map<string, FolderTreeNode>();
    const walk = (nodes: FolderTreeNode[]) => {
      for (const n of nodes) {
        map.set(n.id, n);
        if (n.children) walk(n.children);
      }
    };
    walk(tree);
    return map;
  }, [tree]);

  const toTreeData = (nodes: FolderTreeNode[]): DataNode[] =>
    nodes.map((node) => {
      const totalCount = node.knowledge_count + node.file_document_count;
      const isRenaming = renamingId === node.id;

      return {
        key: node.id,
        title: isRenaming ? (
          <Input
            size="small"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onPressEnter={handleRenameConfirm}
            onBlur={handleRenameConfirm}
            autoFocus
            style={{ width: 140 }}
          />
        ) : (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 4,
              width: '100%',
              minWidth: 0,
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              {expandedKeys.includes(node.id) ? <FolderOpenOutlined /> : <FolderOutlined />}
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {node.name}
              </span>
              {totalCount > 0 && (
                <Badge
                  count={totalCount}
                  size="small"
                  style={{ backgroundColor: '#d9d9d9', color: '#595959', fontSize: 10 }}
                />
              )}
            </span>
            <Dropdown menu={{ items: getContextMenuItems(node) }} trigger={['click', 'contextMenu']}>
              <Tooltip title="資料夾操作">
                <Button
                  type="text"
                  size="small"
                  icon={<MoreOutlined />}
                  style={{ flex: '0 0 auto', width: 22, height: 22 }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                />
              </Tooltip>
            </Dropdown>
          </span>
        ),
        children: node.children?.length ? toTreeData(node.children) : undefined,
      };
    });

  const treeData: DataNode[] = [
    {
      key: '__root__',
      title: (
        <Dropdown
          menu={{
            items: [{
              key: 'new-root',
              icon: <PlusOutlined />,
              label: '新增資料夾',
              onClick: () => handleCreateStart(null),
            }],
          }}
          trigger={['contextMenu']}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <FolderOpenOutlined style={{ color: '#1677ff' }} />
            <Text strong>所有知識</Text>
          </span>
        </Dropdown>
      ),
      children: toTreeData(tree),
    },
  ];

  const handleSelect: TreeProps['onSelect'] = (keys) => {
    if (keys.length === 0) return;
    const key = keys[0] as string;
    onSelect(key === '__root__' ? null : key);
  };

  const handleExpand: TreeProps['onExpand'] = (keys) => {
    onExpandedKeysChange(keys.filter(k => k !== '__root__') as string[]);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '8px 8px 4px', borderBottom: '1px solid #f0f0f0' }}>
        <Tooltip title="新增資料夾">
          <Button
            type="dashed"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => handleCreateStart(selectedKey)}
            block
          >
            新增資料夾
          </Button>
        </Tooltip>
      </div>

      {/* Inline new folder input */}
      {creatingInParent !== undefined && (
        <div style={{ padding: '6px 8px', borderBottom: '1px solid #f5f5f5' }}>
          <Space.Compact style={{ width: '100%' }}>
            <Input
              size="small"
              placeholder="資料夾名稱"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onPressEnter={handleCreateConfirm}
              autoFocus
            />
            <Button size="small" type="primary" onClick={handleCreateConfirm}>
              確定
            </Button>
            <Button size="small" onClick={() => setCreatingInParent(undefined)}>
              取消
            </Button>
          </Space.Compact>
          {creatingInParent && (
            <Text type="secondary" style={{ fontSize: 11, marginTop: 2, display: 'block' }}>
              建立在：{nodeMap.get(creatingInParent)?.name ?? '根目錄'}
            </Text>
          )}
        </div>
      )}

      <div style={{ flex: 1, overflow: 'auto', padding: '4px 0' }}>
        <Tree
          showLine={{ showLeafIcon: false }}
          defaultExpandAll
          selectedKeys={selectedKey ? [selectedKey] : ['__root__']}
          expandedKeys={['__root__', ...expandedKeys]}
          treeData={treeData}
          onSelect={handleSelect}
          onExpand={handleExpand}
          blockNode
          style={{ fontSize: 13 }}
        />
      </div>
    </div>
  );
};

export default FolderTree;
